import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VoteButtons from "@/components/VoteButtons";

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function CommentSection({ parentType, parentId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/comments`, { params: { parent_type: parentType, parent_id: parentId } });
      setComments(data);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [parentId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post("/comments", { parent_type: parentType, parent_id: parentId, body: body.trim() });
      if (data.status === "blocked") {
        toast.warning(data.message || "Your comment was sent for review.");
      } else {
        setComments((c) => [...c, data]);
        toast.success("Comment posted");
      }
      setBody("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div data-testid="comment-section" className="mt-10">
      <h3 className="font-heading font-extrabold text-white text-xl mb-4 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-cyan-400" /> Comments ({comments.length})
      </h3>

      {user ? (
        <form onSubmit={submit} className="flex gap-3 mb-8" data-testid="comment-form">
          <textarea
            data-testid="comment-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Share your thoughts..."
            rows={2}
            className="flex-1 bg-[#0B1020] border border-[#232A4D] rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/60 resize-none"
          />
          <button
            data-testid="comment-submit"
            disabled={posting || !body.trim()}
            className="px-5 rounded-xl btn-neon font-semibold flex items-center gap-2 disabled:opacity-50"
          >
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      ) : (
        <div className="glass rounded-xl p-4 mb-8 text-sm text-slate-300">
          <Link to="/login" className="text-cyan-400 font-semibold">Log in</Link> to join the conversation.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
      ) : comments.length === 0 ? (
        <p className="text-slate-500 text-sm">No comments yet. Be the first to reply.</p>
      ) : (
        <div className="space-y-4">
          {comments.map((c) => (
            <div key={c.id} data-testid={`comment-${c.id}`} className="flex gap-3 glass rounded-xl p-4">
              <VoteButtons type="comment" item={c} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-white">{c.author_name}</span>
                  <span className="text-xs text-slate-500">· {timeAgo(c.created_at)}</span>
                </div>
                <p className="text-sm text-slate-300 whitespace-pre-wrap break-words">{c.body}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
